import React, { useEffect, useRef, useState } from 'react'
import { Redirect, useLocation } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import flowRight from 'lodash/flowRight'
import get from 'lodash/get'

import { Loading } from '@fielded/shared-ui'

import { findShipments, selectShipments } from '../../../../../../root/reducers/shipments'
import { hasFeature } from '../../../../../../van-shared/utils/features'
import { withApi } from '../../../../../../common/ApiProvider'
import { withUser } from '../../../../../../common/AuthenticationProvider'
import withConfig from '../../../../../../van-shared/hoc/withConfig'
import { fetchMasterData, selectHasReceivedMasterData } from '../../../../../../root/reducers/shipments/master-data'
import AdjustPickup from './AdjustPickup'
import ConfirmPickUp from './ConfirmPickUp'

const ViewType = {
  ERROR: 'error',
  // First step, driver can adjust qty of items if the prepared packages don't have the amount described.
  ADJUSTMENTS: 'adjustments',
  // Second step, Warehouse user signs the qty they are giving away
  CONFIRMATION: 'confirmation'
}

const ConfirmPickUpContainer = ({
  api,
  user,
  config,
  history
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const hasReceivedMasterData = useSelector(selectHasReceivedMasterData)
  const shipmentsFromStore = useSelector(selectShipments)

  if (!location.state) {
    return <Redirect to='/' />
  }

  const [viewType, setViewType] = useState(ViewType.ADJUSTMENTS)
  const [loading, setLoading] = useState(true)
  const [formFieldsInfo, setFormFieldsInfo] = useState({})
  const formRef = useRef()
  const signatureFormRef = useRef()

  const { selectedDeliveriesIds } = location.state
  const selectedDeliveries = selectedDeliveriesIds.map((id) => shipmentsFromStore[id])
  // const withAdjustmentsFeature = hasFeature(config.features, 'allowAdjustmentsAtPickup')
  const withAdjustmentsFeature = true // temoprarily removing restriction for it being available to epn only

  // We check if any of the deliveries has any count with adjustment
  const isAdjusted = selectedDeliveries.some((delivery) => {
    return Object.values(get(delivery, 'counts', {})).some((product) => !!product.adjustment)
  })

  const driverName = get(user, 'fullName', '')

  const initialize = async () => {
    if (!hasReceivedMasterData) {
      await dispatch(fetchMasterData())
    }
    setLoading(false)
  }

  const onGoToConfirmation = async () => {
    const formValidationErrors = await formRef.current.validate()
    const fields = formRef.current.getFields()
    if (!formValidationErrors) {
      setFormFieldsInfo(fields)
      setViewType(ViewType.CONFIRMATION)
    }
  }

  const onProceed = async () => {
    const signatureField = signatureFormRef.current.getFields()

    const payload = {
      ...signatureField,
      ...formFieldsInfo,
      shipments: selectedDeliveries
    }

    // Advance selected shipments
    try {
      const updatedSnapshots = await api.shipment.bulkAdvanceSnapshots(payload)
      if (updatedSnapshots && updatedSnapshots.length) {
        const userLocation = user.location
        const withListInGeoLocation = hasFeature(config.features, 'shipments:listInGeoLocation')
        const useRouteSpecificFilter = true
        const options = { noExternalShipments: true }

        // Update store
        await dispatch(
          findShipments(
            userLocation,
            withListInGeoLocation,
            useRouteSpecificFilter,
            options
          )
        )
      }
      history.push('/shipments/list')
    } catch (e) {
      throw new Error(`Error found: ${e}`)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  if (loading) {
    return <Loading />
  }

  if (viewType === ViewType.ADJUSTMENTS) {
    return (
      <AdjustPickup
        formRef={formRef}
        history={history}
        deliveries={selectedDeliveries}
        driverName={driverName}
        onProceed={onGoToConfirmation}
        isAdjusted={isAdjusted && withAdjustmentsFeature}
        withAdjustmentsFeature={withAdjustmentsFeature}
      />
    )
  }

  if (viewType === ViewType.CONFIRMATION) {
    return (
      <ConfirmPickUp
        signatureFormRef={signatureFormRef}
        history={history}
        config={config}
        deliveries={selectedDeliveries}
        driverName={driverName}
        onProceed={onProceed}
        isAdjusted={isAdjusted && withAdjustmentsFeature}
        withAdjustmentsFeature={withAdjustmentsFeature}
      />
    )
  }
}

const withHOCs = flowRight(
  withApi,
  withUser,
  withConfig
)

export default withHOCs(ConfirmPickUpContainer)
